import api from '../config/api';
import axios from 'axios';
import apiInstance from './api';
import { Creneau } from './creneaux.service';

export interface Consultant {
  id: number;
  name: string; // This will be the company_name for consultants
  company_name?: string; // Additional field for consultants
  logo?: string;
  phone_number: string;
  address?: string;
  about?: string;
  description?: string; // Backend returns this field
  website?: string;
  facebook_url?: string;
  linkedin_url?: string;
  social_media_links?: string;
  field_ids?: number[];
  category_id?: string;
  country_id?: number; // Foreign key to countries table
  country?: { // Joined country object
    id: number;
    name: string;
    code?: string;
  };
  city?: string;
  wilaya_id?: number;
  wilaya?: string | { name: string }; // Backend returns wilaya as string or object
  specialties?: string[]; // Backend returns specialties array
  profile_type: 'training_center' | 'consultant';
  verification_status?: 'pending' | 'verified' | 'rejected';
  rejection_reason?: string;
  views: number;
  rating: number;
  reviews_count: number;
  reviewCount?: number; // Backend returns this field
  is_active: boolean;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
  totalTrainings?: number; // Backend returns this field
  user: {
    id: number;
    email: string; // Email is stored in the user table
  };
  trainings?: ConsultantService[]; // Services are stored as trainings with service_type = 'consulting_service'
  reviews?: ConsultantReview[];
}

export interface ConsultantService {
  id: string;
  title: string;
  reference?: string;
  country_id?: number; // Foreign key to countries table
  country?: { // Joined country object
    id: number;
    name: string;
    code?: string;
  };
  city?: string;
  wilaya_id?: number;
  domaine_id: number;
  subcategory_id?: string;
  format: string;
  language?: string;
  estimated_duration?: string;
  duration?: string;
  full_description: string;
  skills_gained?: string;
  learning_objectives?: string;
  target_audience?: string;
  consultant_names?: string;
  availability_calendar?: string;
  banner_image?: string;
  detailed_program_pdf?: string;
  service_type: 'training' | 'consulting_service';
  training_center_id: number;
  status: 'pending' | 'approved' | 'refused';
  rejection_reason?: string;
  views: number;
  rating: number;
  reviews: number;
  tags?: string[];
  is_featured: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  domaine: {
    id: number;
    name_french: string;
  };
  subcategory?: {
    id: string;
    name_french: string;
  };
  wilaya?: {
    id: number;
    name: string;
  };
  training_center?: Consultant; // The consultant who offers this service
  creneaux?: Creneau[]; // Créneaux for this service
}

export interface ConsultantReview {
  id: number;
  user_id: number;
  consultant_id: number;
  rating: number;
  comment?: string;
  created_at: string;
  updated_at: string;
  user: {
    id: number;
    email: string;
  };
}

export interface CreateConsultantDto {
  company_name: string;
  logo?: string;
  phone_number: string;
  address?: string;
  about?: string;
  website?: string;
  social_media_links?: string;
  field_ids?: number[];
  category_id?: string;
  country?: string;
  wilaya_id?: number;
}

export interface UpdateConsultantDto {
  company_name?: string;
  logo?: string | File;
  phone_number?: string;
  email?: string;
  address?: string;
  about?: string;
  website?: string;
  social_media_links?: string;
  field_ids?: number[];
  category_id?: string;
  country?: string;
  wilaya_id?: number;
}

export interface CreateConsultantServiceDto {
  title: string;
  reference?: string;
  country?: string;
  city?: string;
  wilaya_id?: number;
  domaine_id: number; // Backend expects number
  subcategory_id?: string;
  format: 'presentiel' | 'distance' | 'hybride';
  language?: string;
  estimated_duration?: string;
  full_description: string;
  skills_gained?: string; 
  learning_objectives?: string; 
  target_audience?: string; 
  consultant_names?: string;
  banner_image?: string;
  detailed_program_pdf?: string; 
}

export interface ConsultantFilters {
  search?: string;
  country?: number; // Changed to number for country ID
  category_id?: string;
  field_id?: number;
  wilaya_id?: number;
  verification_status?: string;
  sortBy?: 'name' | 'services' | 'views' | 'trainings';
  page?: number;
  limit?: number;
}

export interface ConsultantServiceFilters {
  consultant_id?: number;
  domaine_id?: number;
  wilaya_id?: number;
  format?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export const consultantsService = {
  // Consultant management - using unified training centers endpoint with profile_type filter
  getAllConsultants: async (filters: ConsultantFilters = {}): Promise<{
    consultants: Consultant[];
    total: number;
  }> => {
    try {
      const params = new URLSearchParams();
      
      params.append('profile_type', 'consultant'); // Filter for consultants only
      if (filters.search) params.append('search', filters.search);
      if (filters.country) params.append('country', filters.country.toString()); // Send country ID
      if (filters.category_id) params.append('specialty', filters.category_id); // Backend uses 'specialty' for category_id
      if (filters.field_id) params.append('specialty', filters.field_id.toString()); // Backend uses 'specialty' not 'field_id'
      if (filters.wilaya_id) params.append('wilaya', filters.wilaya_id.toString()); // Backend uses 'wilaya' not 'wilaya_id'
      if (filters.verification_status) params.append('verified', filters.verification_status === 'verified' ? 'true' : 'false');
      // Map frontend sortBy to backend sortBy
      if (filters.sortBy) {
        const backendSortBy = filters.sortBy === 'services' ? 'trainings' : filters.sortBy;
        params.append('sortBy', backendSortBy);
      }
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());

      const response = await axios.get(`${api.BASE_URL}/public/training-centers?${params.toString()}`);
      return {
        consultants: response.data.centers || [],
        total: response.data.pagination?.total || 0
      };
    } catch (error) {
      throw error;
    }
  },

  getConsultant: async (id: number): Promise<Consultant> => {
    try {
      const response = await axios.get(`${api.BASE_URL}/consultants/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createConsultant: async (data: CreateConsultantDto): Promise<Consultant> => {
    try {
      const response = await apiInstance.post('/training-centers', {
        ...data,
        profile_type: 'consultant',
        name: data.company_name, // Use company_name as name
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Error creating consultant');
      }
      throw error;
    }
  },

  updateConsultant: async (id: number, data: UpdateConsultantDto): Promise<Consultant> => {
    try {
      const response = await apiInstance.put(`/consultants/${id}`, data);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Error updating consultant');
      }
      throw error;
    }
  },

  getMyProfile: async (): Promise<Consultant> => {
    try {
      const response = await apiInstance.get('/consultants/my/profile');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateMyProfile: async (data: UpdateConsultantDto): Promise<Consultant> => {
    try {
      // Check if data contains a file (logo)
      if (data.logo && typeof data.logo === 'object' && data.logo instanceof File) {
        // Create FormData for file upload
        const formData = new FormData();
        
        // Add all non-file fields
        const logoFile = data.logo as File;
        const { logo, ...restData } = data;
        
        Object.keys(restData).forEach(key => {
          const value = (restData as any)[key];
          if (value !== null && value !== undefined) {
            formData.append(key, value);
          }
        });
        
        // Add logo file
        formData.append('logo', logoFile);
        
        const response = await apiInstance.patch('/consultants/my/profile', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      } else {
        // Regular JSON update
        const response = await apiInstance.patch('/consultants/my/profile', data);
        return response.data;
      }
    } catch (error: any) {
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Error updating profile');
      }
      throw error;
    }
  },

  // Consultant Services management
  getAllServices: async (filters: ConsultantServiceFilters = {}): Promise<{
    services: ConsultantService[];
    total: number;
  }> => {
    try {
      // If consultant_id is provided, use the public consultant services endpoint
      if (filters.consultant_id) {
        const params = new URLSearchParams();
        
        if (filters.domaine_id) params.append('domaine_id', filters.domaine_id.toString());
        if (filters.wilaya_id) params.append('wilaya_id', filters.wilaya_id.toString());
        if (filters.format) params.append('format', filters.format);
        if (filters.search) params.append('search', filters.search);
        if (filters.page) params.append('page', filters.page.toString());
        if (filters.limit) params.append('limit', filters.limit.toString());

        const response = await axios.get(`${api.BASE_URL}/consultants/${filters.consultant_id}/services?${params.toString()}`);
        return {
          services: response.data.services || [],
          total: response.data.total || 0
        };
      }

      // Fallback to the protected endpoint for general service listing
      const params = new URLSearchParams();
      
      if (filters.domaine_id) params.append('domaine_id', filters.domaine_id.toString());
      if (filters.wilaya_id) params.append('wilaya_id', filters.wilaya_id.toString());
      if (filters.format) params.append('format', filters.format);
      if (filters.search) params.append('search', filters.search);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());

      const response = await axios.get(`${api.BASE_URL}/trainings/consultant-services?${params.toString()}`);
      return {
        services: response.data || [],
        total: response.data?.length || 0
      };
    } catch (error) {
      throw error;
    }
  },

  getService: async (id: string): Promise<ConsultantService> => {
    try {
      const response = await axios.get(`${api.BASE_URL}/consultants/services/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createService: async (data: CreateConsultantServiceDto, files?: { banner?: File; pdf?: File }): Promise<ConsultantService> => {
    try {
      const formData = new FormData();
      
      // Append all form data
      Object.keys(data).forEach(key => {
        const value = data[key as keyof CreateConsultantServiceDto];
        if (value !== undefined && value !== null) {
          // Special handling for numbers to ensure they're sent as numbers
          if (key === 'domaine_id' || key === 'wilaya_id') {
            formData.append(key, String(value));
          } else {
            formData.append(key, String(value));
          }
        }
      });
      
      // Append files if provided (backend expects 'files' field name)
      if (files?.banner) {
        formData.append('files', files.banner);
      }
      if (files?.pdf) {
        formData.append('files', files.pdf);
      }
      
      const response = await apiInstance.post('/trainings/consultant-services', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Error creating service');
      }
      throw error;
    }
  },

  updateService: async (id: string, data: Partial<CreateConsultantServiceDto>): Promise<ConsultantService> => {
    try {
      const response = await apiInstance.patch(`/trainings/consultant-services/${id}`, data);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Error updating service');
      }
      throw error;
    }
  },

  deleteService: async (id: string): Promise<void> => {
    try {
      await apiInstance.delete(`/trainings/consultant-services/${id}`);
    } catch (error) {
      throw error;
    }
  },

  getMyServices: async (): Promise<{
    services: ConsultantService[];
    total: number;
  }> => {
    try {
      const response = await apiInstance.get('/consultants/my/services');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getMyServicesWithCreneaux: async (): Promise<ConsultantService[]> => {
    try {
      const response = await apiInstance.get('/trainings/my/services-with-creneaux');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Reviews
  getConsultantReviews: async (consultantId: number): Promise<ConsultantReview[]> => {
    try {
      const response = await axios.get(`${api.BASE_URL}/consultants/${consultantId}/reviews`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createConsultantReview: async (consultantId: number, data: { rating: number; comment?: string }): Promise<ConsultantReview> => {
    try {
      const response = await apiInstance.post(`/consultants/${consultantId}/reviews`, data);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Error creating review');
      }
      throw error;
    }
  },

  // Stats
  getConsultantStats: async (): Promise<{
    totalConsultants: number;
    averageRating: number;
  }> => {
    try {
      const response = await axios.get(`${api.BASE_URL}/consultants/stats`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};
